import { Button, Dropdown, type MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_OPTIONS, LANGUAGE_STORAGE_KEY, isAppLanguage, type AppLanguage } from '../../i18n/config';
import './LanguageSwitcher.css';

type LanguageSwitcherProps = {
    compact?: boolean;
};

function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
    const { t, i18n } = useTranslation('common');
    const language = (i18n.resolvedLanguage || i18n.language) as AppLanguage;
    const selectedLanguage =
        LANGUAGE_OPTIONS.find((item) => item.key === language) || LANGUAGE_OPTIONS[0];
    const items: MenuProps['items'] = LANGUAGE_OPTIONS.map((item) => ({
        key: item.key,
        label: (
            <span className="language-switcher__option">
                <span aria-hidden>{item.flag}</span>
                <span>{item.nativeName}</span>
            </span>
        ),
    }));

    const selectLanguage: MenuProps['onClick'] = ({ key }) => {
        if (isAppLanguage(key)) localStorage.setItem(LANGUAGE_STORAGE_KEY, key);
        void i18n.changeLanguage(key);
    };

    return (
        <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="language-switcher__dropdown"
            menu={{
                items,
                onClick: selectLanguage,
                selectable: true,
                selectedKeys: [language],
            }}
        >
            <Button
                className={`language-switcher${compact ? ' language-switcher--compact' : ''}`}
                aria-label={t('a11y.chooseLanguage')}
            >
                <span aria-hidden>{selectedLanguage.flag}</span>
                <span className="language-switcher__name">{selectedLanguage.nativeName}</span>
                <DownOutlined />
            </Button>
        </Dropdown>
    );
}

export default LanguageSwitcher;
